import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import format from 'string-template';

import {
  Card,
  Container,
  HeaderContainer,
  SubTitle,
  Title,
  TitleContainer,
} from '../../../../../components/v2/Card';
import { StyledTextField, StyledTextFieldContainer } from '../../../../../components/Inputs';
import InputLabel from '../../../../../components/v2/InputLabel';
import SaveButton from '../../../../../components/Button/ActionButtons/SaveButton';
import { isInsideSalesAdmin } from '../../../../../utils/postHog';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import RadioButtonGroup from '../../../../components/RadioButtonGroup';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useToast from '../../../../hooks/useToast';
import CoachConfig, { DEFAULT_RCS_PERCENTAGE, RcsOutreachOptions } from '../../../../Model/CoachConfig';
import { ContactProvider, SalesStage } from '../../../../Model/Lead';
import MsgTemplate from '../../../../../Model/MsgTemplate';
import { SettingOption } from '../../utils';
import { PercentageFieldContainer, StyledAlert, StyledHelperText } from '../AutomatedTextSetter/styles';
import texts from './texts.json';

const radioButtonOptions = [
  { label: texts.enabled, value: SettingOption.ENABLED },
  { label: texts.disabled, value: SettingOption.DISABLED },
];

/**
 * Whether a percentage typed in the field can be saved: a whole number from 0 to 100.
 * @param {string} value - The field's value
 * @return {boolean}
 */
const isValidPercentage = (value) => /^\d+$/.test(value) && Number(value) <= 100;

/**
 * Whether a template is the RCS copy the server needs before it puts any of the coach's leads in the RCS arm: the
 * current, enabled first message for RCS, matched as the server matches it (by stage and provider, not by name).
 * @param {MsgTemplate} template
 * @return {boolean}
 */
const isLiveRcsFirstMessage = (template) => !!template.current && template.isEnabled
  && template.salesStage === SalesStage.FIRST_MESSAGE && template.msgProvider === ContactProvider.RCS;

const RcsOutreach = ({ coachId }) => {
  const [coachConfigDoc, setCoachConfigDoc] = useState(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [rcsPercentage, setRcsPercentage] = useState(`${DEFAULT_RCS_PERCENTAGE}`);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasRcsFirstMessage, setHasRcsFirstMessage] = useState(true);

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { userDoc } = useContext(LoggedInUserContext);
  const isReadOnly = !isInsideSalesAdmin();
  const isPercentageValid = isValidPercentage(rcsPercentage);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      setCoachConfigDoc(null);
      if (!coachId) {
        return;
      }
      const loadSettings = async () => {
        try {
          const coachConfig = await CoachConfig.getCoachConfigByCoachId(coachId);
          if (isComponentMountedRef.current) {
            setCoachConfigDoc(coachConfig);
            setIsEnabled(coachConfig.isRcsOutreachEnabled);
            setRcsPercentage(`${coachConfig.rcsOutreachPercentage}`);
          }
        } catch (error) {
          showToast(format(texts.loadFailed, { error }), { error: true });
        }
      };
      // Only the warning depends on this lookup, so a failure leaves the settings usable and shows no warning.
      const loadRcsFirstMessage = async () => {
        try {
          const templates = await MsgTemplate.getTemplatesByCoach(coachId);
          if (isComponentMountedRef.current) {
            setHasRcsFirstMessage(templates.docs.some(isLiveRcsFirstMessage));
          }
        } catch {
          if (isComponentMountedRef.current) {
            setHasRcsFirstMessage(true);
          }
        }
      };
      await Promise.all([loadSettings(), loadRcsFirstMessage()]);
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    };
    init();
  }, [coachId, isComponentMountedRef, showToast]);

  const onSave = useCallback(async () => {
    if (!coachConfigDoc) {
      showToast(texts.configUnavailable, { error: true });
      return;
    }
    if (!isPercentageValid) {
      showToast(texts.rcsPercentageInvalid, { error: true });
      return;
    }

    setIsSaving(true);
    try {
      await coachConfigDoc.updateRcsOutreachConfig({
        [RcsOutreachOptions.IS_ENABLED]: isEnabled,
        [RcsOutreachOptions.RCS_PERCENTAGE]: Number(rcsPercentage),
      }, userDoc.name);
      showToast(texts.saved, { type: 'success' });
    } catch (error) {
      showToast(format(texts.error, { error }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [
    coachConfigDoc,
    isEnabled,
    isPercentageValid,
    rcsPercentage,
    showToast,
    isComponentMountedRef,
    userDoc,
  ]);

  return (
    <Card>
      <HeaderContainer>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.description}</SubTitle>
        </TitleContainer>
      </HeaderContainer>
      <Container>
        {!isLoading && !hasRcsFirstMessage && (
          <StyledAlert severity="warning">{texts.noRcsFirstMessage}</StyledAlert>
        )}
        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.rcsOutreach}</InputLabel>
          <RadioButtonGroup
            options={radioButtonOptions}
            selectedOption={isEnabled ? SettingOption.ENABLED : SettingOption.DISABLED}
            onOptionChange={(val) => setIsEnabled(val === SettingOption.ENABLED)}
            disabled={isReadOnly}
          />
        </StyledTextFieldContainer>

        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.rcsPercentage}</InputLabel>
          <PercentageFieldContainer>
            <StyledTextField
              value={rcsPercentage}
              onChange={(e) => setRcsPercentage(e.target.value)}
              type="number"
              variant="outlined"
              title="%"
              disabled={isReadOnly}
              error={!isPercentageValid}
              InputProps={{ inputProps: { min: 0, max: 100 } }}
            />
          </PercentageFieldContainer>
          {!isPercentageValid && (
            <StyledHelperText $isError>{texts.rcsPercentageInvalid}</StyledHelperText>
          )}
        </StyledTextFieldContainer>

        <SaveButton
          onClick={onSave}
          disabled={isReadOnly}
          $fitToContent
        >
          {texts.save}
        </SaveButton>
      </Container>
      <LoadingOverlay isLoading={isLoading || isSaving} />
    </Card>
  );
};

RcsOutreach.propTypes = {
  coachId: PropTypes.string,
};

RcsOutreach.defaultProps = {
  coachId: '',
};

export default compose(
  observer,
)(RcsOutreach);
